import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SeoHead from "@/components/SeoHead";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { blogPosts } from "@/data/blogPosts";
import blogHeroAsset from "@/assets/blog-hero-yoga.png.asset.json";
const blogHero = blogHeroAsset.url;

const Blog = () => {
  const heroText = useScrollReveal<HTMLDivElement>();
  const featuredReveal = useScrollReveal<HTMLDivElement>();

  const [featured, ...rest] = blogPosts;

  return (
    <main>
      <SeoHead
        title="The Blog | Wellness Academy USA"
        description="Editorial insights on cardiometabolic health, ancestral nutrition, functional training, longevity, and luxury wellness retreats from Wellness Academy USA."
        image={blogHero}
        path="/blog"
      />

      {/* Hero - full bleed */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <img
          src={blogHero}
          alt="The Blog"
          width={1920}
          height={960}
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/45" />
        <div
          ref={heroText.ref}
          style={heroText.style}
          className="relative z-10 max-w-5xl mx-auto w-full px-6 sm:px-8 py-32 flex flex-col items-center text-center"
        >
          <p className="font-sans text-xs uppercase tracking-[0.3em] text-nav-cta mb-4">
            The Blog
          </p>
          <h1 className="font-serif font-light text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white leading-[1.1] tracking-wide mb-6">
            Insights for a<br />Long, Vital Life
          </h1>
          <p className="font-sans text-base md:text-lg text-white/85 max-w-2xl leading-relaxed">
            Editorial perspectives on cardiometabolic health, ancestral nutrition,
            functional movement, and the art of living well.
          </p>
        </div>
      </section>

      <div className="border-t border-nav-cta/25" />

      {/* Featured post */}
      <section className="bg-background py-20 md:py-[100px]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-[60px]">
          <p className="font-sans text-xs uppercase tracking-[0.2em] text-nav-cta mb-10">
            Featured
          </p>

          <div ref={featuredReveal.ref} style={featuredReveal.style}>
            <Link
              to={`/blog/${featured.slug}`}
              className="group grid md:grid-cols-2 gap-10 md:gap-16 items-center"
            >
              <div className="relative overflow-hidden aspect-[4/3]">
                <img
                  src={featured.image}
                  alt={featured.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                  loading="lazy"
                  width={1280}
                  height={896}
                />
              </div>
              <div>
                <p className="font-sans text-xs uppercase tracking-[0.2em] text-nav-cta mb-5">
                  {featured.category}
                </p>
                <h2 className="font-serif font-light text-3xl md:text-4xl lg:text-5xl text-foreground leading-[1.1] tracking-wide mb-6 group-hover:text-nav-cta transition-colors duration-500">
                  {featured.title}
                </h2>
                <p className="font-sans text-base md:text-lg text-foreground/80 leading-relaxed mb-8">
                  {featured.excerpt}
                </p>
                <div className="flex items-center gap-6 mb-8 font-sans text-sm text-foreground/60">
                  <span>{featured.author}</span>
                  <span className="w-1 h-1 rounded-full bg-foreground/30" />
                  <span>{featured.date}</span>
                  <span className="w-1 h-1 rounded-full bg-foreground/30" />
                  <span>{featured.readTime}</span>
                </div>
                <span className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.2em] text-nav-cta border-b border-nav-cta/40 pb-1 group-hover:border-nav-cta transition-colors">
                  Read the Article
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <div className="border-t border-foreground/10" />

      {/* Grid */}
      <section className="bg-secondary py-20 md:py-[100px]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-[60px]">
          <div className="flex items-baseline justify-between mb-12">
            <p className="font-sans text-xs uppercase tracking-[0.2em] text-nav-cta">
              More from the Blog
            </p>
          </div>

          <div
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12"
          >
            {rest.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group flex flex-col"
              >
                <div className="relative overflow-hidden aspect-[4/5] mb-6">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                    loading="lazy"
                    width={1280}
                    height={896}
                  />
                </div>
                <p className="font-sans text-xs uppercase tracking-[0.2em] text-nav-cta mb-3">
                  {post.category}
                </p>
                <h3 className="font-serif font-light text-2xl md:text-3xl text-foreground leading-tight mb-4 group-hover:text-nav-cta transition-colors duration-500 tracking-wide">
                  {post.title}
                </h3>
                <p className="font-sans text-base text-foreground/75 leading-relaxed mb-5">
                  {post.excerpt}
                </p>
                <div className="mt-auto flex items-center gap-4 font-sans text-xs text-foreground/60">
                  <span>{post.date}</span>
                  <span className="w-1 h-1 rounded-full bg-foreground/30" />
                  <span>{post.readTime}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Blog;
